import React, { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import {
  emailAccount,
  firebaseReady,
  resetAccountPassword,
  signInWithProvider,
} from "../services/firebaseAuth";

const SignInPage: React.FC = () => {
  const [register, setRegister] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const navigate = useNavigate();
  const [params] = useSearchParams();

  const finish = () => navigate(params.get("returnTo") || "/");
  const run = async (action: () => Promise<unknown>) => {
    setBusy(true);
    setError("");
    setNotice("");
    try {
      await action();
      finish();
    } catch {
      setError(
        "We could not complete that sign-in. Check your details and try again.",
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      style={{
        minHeight: "70vh",
        display: "grid",
        placeItems: "center",
        padding: 22,
      }}
    >
      <form
        onSubmit={(e) => {
          e.preventDefault();
          run(() => emailAccount(email, password, register));
        }}
        style={{
          width: "min(440px,100%)",
          padding: 38,
          border: "1px solid rgba(23,35,30,.13)",
          borderRadius: 24,
          background: "#fffdf8",
          boxShadow: "0 22px 70px rgba(41,55,48,.11)",
        }}
      >
        <span className="section-kicker">Your Observer account</span>
        <h1
          style={{
            fontFamily: "Manrope,sans-serif",
            fontSize: 36,
            letterSpacing: "-.055em",
            margin: "12px 0 8px",
          }}
        >
          {register ? "Create your account." : "Welcome in."}
        </h1>
        <p style={{ color: "#66736d", fontSize: 13, lineHeight: 1.6 }}>
          Verify your email to review a teacher, reader, coach, psychic, medium,
          astrologer, or spiritual practitioner—or to manage your relationship
          with a profile.
        </p>
        {!firebaseReady && (
          <p style={messageStyle}>
            Account services are being provisioned. The public directory remains
            available.
          </p>
        )}
        {error && (
          <p
            style={{ ...messageStyle, color: "#9f2f2f", background: "#fff0ed" }}
          >
            {error}
          </p>
        )}
        {notice && <p style={messageStyle}>{notice}</p>}
        <button
          type="button"
          disabled={busy || !firebaseReady}
          onClick={() => run(() => signInWithProvider("apple"))}
          className="button"
          style={providerButton}
        >
          <AppleMark />
          <span>Continue with Apple</span>
        </button>
        <button
          type="button"
          disabled={busy || !firebaseReady}
          onClick={() => run(() => signInWithProvider("google"))}
          className="button"
          style={{ ...providerButton, marginTop: 10 }}
        >
          <GoogleMark />
          <span>Continue with Google</span>
        </button>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 12,
            margin: "23px 0",
            color: "#89938e",
            fontSize: 10,
          }}
        >
          <span style={rule} />
          OR
          <span style={rule} />
        </div>
        <label style={label}>
          Email address
          <input
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            type="email"
            autoComplete="email"
            required
            style={input}
          />
        </label>
        <label style={label}>
          Password
          <input
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            type="password"
            minLength={8}
            autoComplete={register ? "new-password" : "current-password"}
            required
            style={input}
          />
        </label>
        <button
          disabled={busy || !firebaseReady}
          className="button button-primary"
          style={{ width: "100%", border: 0, marginTop: 14 }}
        >
          {busy
            ? "One moment…"
            : register
              ? "Create account"
              : "Continue with email"}
        </button>
        {!register && (
          <button
            type="button"
            disabled={!email || busy || !firebaseReady}
            onClick={async () => {
              setError("");
              try {
                await resetAccountPassword(email);
                setNotice(
                  "If that account exists, a reset link has been sent.",
                );
              } catch {
                setError("Enter your email, then try the reset again.");
              }
            }}
            style={textButton}
          >
            Forgot password?
          </button>
        )}
        <button
          type="button"
          onClick={() => {
            setRegister(!register);
            setError("");
            setNotice("");
          }}
          style={textButton}
        >
          {register
            ? "Already have an account? Sign in"
            : "New to Observer Theory? Create an account"}
        </button>
        <p
          style={{
            marginTop: 20,
            color: "#7a8580",
            fontSize: 10,
            lineHeight: 1.6,
          }}
        >
          By continuing, you accept the <Link to="/terms">Terms</Link> and{" "}
          <Link to="/privacy">Privacy Policy</Link>. YouTube, Apple, and Google
          accounts are never used to manufacture or influence reviews.
        </p>
      </form>
    </div>
  );
};

const providerButton: React.CSSProperties = {
  width: "100%",
  minHeight: 48,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  gap: 12,
  border: "1px solid #3c4742",
  borderRadius: 10,
  marginTop: 18,
  background: "#fff",
  color: "#17231e",
  fontSize: 14,
  fontWeight: 600,
};
const providerIcon: React.CSSProperties = {
  width: 19,
  height: 19,
  flex: "0 0 19px",
};

const AppleMark: React.FC = () => (
  <svg
    aria-hidden="true"
    focusable="false"
    viewBox="0 0 24 24"
    style={providerIcon}
  >
    <path
      fill="currentColor"
      d="M16.37 12.53c-.02-2.32 1.9-3.44 1.98-3.49a4.25 4.25 0 0 0-3.36-1.82c-1.41-.15-2.78.84-3.49.84-.73 0-1.82-.83-3-.8a4.42 4.42 0 0 0-3.74 2.28c-1.62 2.81-.41 6.94 1.15 9.21.78 1.11 1.69 2.34 2.87 2.3 1.15-.05 1.58-.74 2.97-.74 1.38 0 1.78.74 2.98.71 1.24-.02 2.02-1.11 2.77-2.23a9.18 9.18 0 0 0 1.26-2.58 4 4 0 0 1-2.39-3.68ZM14.08 5.73A4.04 4.04 0 0 0 15.01 2.84a4.13 4.13 0 0 0-2.67 1.38 3.86 3.86 0 0 0-.95 2.79 3.4 3.4 0 0 0 2.69-1.28Z"
    />
  </svg>
);

const GoogleMark: React.FC = () => (
  <svg
    aria-hidden="true"
    focusable="false"
    viewBox="0 0 24 24"
    style={providerIcon}
  >
    <path
      fill="#4285F4"
      d="M21.35 12.21c0-.7-.06-1.22-.2-1.77H12v3.31h5.37a4.65 4.65 0 0 1-1.99 2.99l-.02.11 2.89 2.24.2.02c1.83-1.69 2.9-4.18 2.9-6.9Z"
    />
    <path
      fill="#34A853"
      d="M12 21.75c2.62 0 4.81-.86 6.41-2.64l-3.05-2.37c-.82.55-1.91.94-3.36.94a5.83 5.83 0 0 1-5.52-4.03l-.11.01-3 2.31-.04.11A9.69 9.69 0 0 0 12 21.75Z"
    />
    <path
      fill="#FBBC05"
      d="M6.48 13.65A5.98 5.98 0 0 1 6.16 12c0-.58.11-1.13.31-1.65v-.12L3.44 7.88l-.1.05A9.75 9.75 0 0 0 2.25 12c0 1.47.39 2.86 1.08 4.08l3.15-2.43Z"
    />
    <path
      fill="#EA4335"
      d="M12 6.32c1.82 0 3.05.79 3.75 1.44l2.72-2.66A9.21 9.21 0 0 0 12 2.25a9.69 9.69 0 0 0-8.67 5.68l3.14 2.42A5.85 5.85 0 0 1 12 6.32Z"
    />
  </svg>
);
const input: React.CSSProperties = {
  width: "100%",
  boxSizing: "border-box",
  marginTop: 7,
  padding: "14px 15px",
  border: "1px solid rgba(23,35,30,.13)",
  borderRadius: 11,
  background: "#fff",
  // iOS zooms focused inputs below 16px and can preserve that zoom after a
  // route change, making the authenticated destination appear clipped.
  fontSize: 16,
};
const label: React.CSSProperties = {
  display: "block",
  marginTop: 12,
  color: "#425049",
  fontSize: 12,
  fontWeight: 700,
};
const rule: React.CSSProperties = {
  flex: 1,
  height: 1,
  background: "rgba(23,35,30,.13)",
};
const messageStyle: React.CSSProperties = {
  padding: 11,
  borderRadius: 10,
  background: "#eef4ed",
  color: "#315d4d",
  fontSize: 12,
};
const textButton: React.CSSProperties = {
  display: "block",
  width: "100%",
  marginTop: 13,
  padding: 0,
  border: 0,
  background: "transparent",
  color: "#315d4d",
  cursor: "pointer",
  fontSize: 12,
};

export default SignInPage;
